/**
 * Locations API Client
 *
 * Fetches locations from Strapi filtered by location type.
 * Also resolves which locations belong to factories for the header selector.
 */

import { getHttpClient } from '@spcc/core'
import type { LocationSummary } from '../../domain/types'
import { transformToLocationSummary } from '../../api/transformers'

/**
 * Fetch active factory & office locations for UI selectors (public, no auth).
 * Used by the portal header LocationSelector and visit booking forms.
 */
export async function fetchWorkingSites(): Promise<LocationSummary[]> {
  const http = getHttpClient()
  // Locale is added once per GET by HttpClient (setLocaleProvider); do not append ?locale= here
  const response = await http.get<any>('/api/working-sites')
  const items = response?.data?.data ?? response?.data ?? []

  const locations: LocationSummary[] = Array.isArray(items)
    ? items.map(transformToLocationSummary)
    : []

  return locations
}

/**
 * Fetch locations for the header selector with factory resolution.
 * Calls /api/working-sites (public, not tenant-scoped) then resolves
 * factoryDocumentId for each location via a batch factory lookup.
 */
export async function fetchHeaderLocations(): Promise<LocationSummary[]> {
  const http = getHttpClient()

  const locations = await fetchWorkingSites()

  // Resolve factoryDocumentId for each location
  const factoryResponse = await http.get<any>(
    '/api/factories?populate[location][fields][0]=documentId&pagination[pageSize]=200&fields[0]=documentId&fields[1]=name'
  )
  const factories = factoryResponse?.data?.data ?? factoryResponse?.data ?? []
  const locationToFactory = new Map<string, string>()
  const locationToFactoryName = new Map<string, string>()
  if (Array.isArray(factories)) {
    for (const factory of factories) {
      const locDocId = factory.location?.documentId
      if (locDocId) {
        locationToFactory.set(locDocId, factory.documentId)
        if (typeof factory.name === 'string' && factory.name.trim()) {
          locationToFactoryName.set(locDocId, factory.name)
        }
      }
    }
  }

  return locations.map((loc) => ({
    ...loc,
    factoryDocumentId: locationToFactory.get(loc.documentId) ?? null,
    factoryName: locationToFactoryName.get(loc.documentId) ?? null,
  }))
}
